import { Inject, Injectable, LOCALE_ID, OnDestroy, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AccountInfo, EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { CookieService } from 'ngx-cookie-service';
import { Subject, filter, finalize, map, takeUntil, zip } from 'rxjs';

import { environment } from 'src/environments/environment';
import { loginRequest, tokenRequest } from '../shared/auth-config';

import { BackendService } from './backend.service';
import { LocalStorageService } from './local-storage.service';
import { UserProfileService } from './user-profile.service';
import { UserStateService } from './user-state.service';

import { IdTokenClaim } from '../types/id-token-claims';

/** service to manage user authentication */
@Injectable( {
    providedIn: 'root'
} )
export class AuthService implements OnDestroy {
    /** flag, if loggedin */
    isLoggedIn = signal( this.hasActiveAccount() );
    /** flag, if login should be disabled */
    loginDisabled = signal( false );

    /**  lifecycle hook */
    readonly #destroyed: Subject<void> = new Subject<void>();

    #cookieService = inject( CookieService );
    #broadcastService = inject( MsalBroadcastService );
    #backendService = inject( BackendService );
    #userProfileService = inject( UserProfileService );
    #userState = inject( UserStateService );
    #router = inject( Router );

    readonly currentUser = this.#userState.state.asReadonly();

    /** computed role signals */
    isAdmin = computed( () => this.currentUser()?.role === 'Scope_Administrator' );
    isSuperAdmin = computed( () => this.currentUser()?.role === 'RSI_Super_Administrator' );
    isScopeUser = computed( () => this.currentUser()?.role === 'Scope_User' );

    /**
     * on init the app listens on msal broadcast service
     * which returns an event message of login state
     *
     * in case of success
     * - session is stored in localStorage
     * - and loginState is set to trigger frontend updates
     * @param {string} locale code
     * @param localStorageService localstorage service
     */
    constructor(
        @Inject( LOCALE_ID ) public locale: string,
        private msalService: MsalService,
        private localStorageService: LocalStorageService
    ) {
        /** listen on msal broadcast service */
        this.handleMsalEvents();

        // restore account after reload when logged in
        if ( this.isLoggedIn() ) {
            const account = this.localStorageService.load( 'account' );
            if ( account && account.organization ) {
                this.#userState.setState( account );
            }
            // logout user, when account does not include required organization details
            else {
                this.handleLogout();
            }
        }
    }

    /**
     * login function
     * which redirects to MS Azure SSO Login
     * when successful, user will get redirected back to app
     */
    login() {
        this.loginDisabled.set( true );
        this.#broadcastService.inProgress$
            .pipe(
                filter( ( status: InteractionStatus ) => status === InteractionStatus.None ),
                takeUntil( this.#destroyed )
            )
            .subscribe( () => {
                this.msalService.loginRedirect( loginRequest );
            } );
    }

    /**
     * logout function
     * redirects to login
     */
    logout() {
        this.loginDisabled.set( true );
        this.msalService.logoutRedirect();
    }

    /**
     * helper function, get active account
     * @returns {boolean} flag, if account is active
     */
    hasActiveAccount(): boolean {
        return this.msalService.instance.getActiveAccount() !== null;
    }

    /**
     * helper function, get Azure token
     * @param {string} tokenType type of token
     * @returns {string} token
     */
    async getTokenType( tokenType: any ): Promise<string> {
        await this.msalService.instance.initialize();
        const activeAccount = this.msalService.instance.getActiveAccount();

        // force cache refresh and set refresh token lifetime
        // 2 hours * 60 minutes * 60 seconds = 7200 seconds
        const authTokenRequest = {
            ...tokenRequest,
            tokenType,
            activeAccount,
            refreshTokenExpirationOffsetSeconds: 7200
        };

        let token = '';
        await this.msalService.instance
            .acquireTokenSilent( authTokenRequest )
            .then( ( response ) => {
                // save silently aquired token
                if ( response['accessToken'] || response['accessToken'] !== '' ) {
                    this.localStorageService.save( 'session', response );
                    token = response['accessToken'] as string;
                }
                // or fallback to interaction when silent call fails
                else {
                    this.msalService.instance.acquireTokenRedirect( authTokenRequest );
                }
            } )
            .catch( async ( error ) => {
                // brute force logout
                this.logout();
                throw error;
            } );

        return token;
    }

    /**
     * helper function to retrieve accessToken
     *
     * @returns {string} accessToken to validate access for current user
     */
    retrieveAccessToken() {
        return this.getTokenType( 'accessToken' );
    }

    /**
     * helper function to retrieve idToken
     *
     * NOTE: this function will be used once the tenant switch got sorted in backend
     * @returns {string} idToken to identify the tenant
     */
    retrieveIdentityToken() {
        return this.getTokenType( 'idToken' );
    }

    /**
     * switch scope tenant
     *
     * NOTE: this function will be used once the tenant switch got sorted in backend
     * @param {string} scopeTID id of scope tenant
     */
    async switchTenant( scopeTID: string ) {
        this.retrieveIdentityToken().then( async ( token: string | undefined ) => {
            console.log( 'IdentityToken', token );
            await this.msalService.instance.clearCache();
            console.log( 'Cache cleared' );

            await this.msalService.instance
                .ssoSilent( {
                    extraQueryParameters: {
                        id_token_hint: token || '',
                        scopeTID
                    },
                    ...tokenRequest
                } )
                .then( ( response ) => {
                    console.log( 'sso silent', response );
                    this.handleAuthResponse( response );
                } )
                .catch( async ( err ) => {
                    console.log( 'catched error', err );
                    await this.msalService.instance
                        .loginRedirect( {
                            extraQueryParameters: {
                                id_token_hint: token || '',
                                scopeTID
                            },
                            ...tokenRequest
                        } )
                        .then( ( response ) => {
                            console.log( 'login redirect', response );
                            this.handleAuthResponse( response );
                        } );
                } );
        } );
    }

    /**
     * helper function, set account after login
     * @param {object} response login payload
     */
    async handleAuthResponse( response: any ) {
        this.msalService.instance.setActiveAccount( response );

        const { name, homeAccountId } = response as AccountInfo;
        const {
            rsiRole: role,
            // scopeTID: tenantID,
            rsiOrganization: organizationOID,
            organizationName = 'Scope'
        } = response?.idTokenClaims as unknown as IdTokenClaim;

        const OID = homeAccountId.indexOf( '-b2c_1a' )
            ? homeAccountId.substring( 0, homeAccountId.indexOf( '-b2c_1a' ) )
            : homeAccountId;

        const organization$ = this.#backendService.getScopeOrganisation( organizationOID );
        const user$ = this.#userProfileService.getUserProfile( OID );
        // NOTE: tenant$ will be used once the tenant switch got sorted in backend
        // const tenant$ = this.#backendService.getScopeTenants(OID);

        // merge api call responses into user object
        let isLoggedIn = false;
        zip( organization$, user$ )
            .pipe(
                map( ( [organization, user] ) => ( { organization, user } ) ),
                finalize( () => this.isLoggedIn.set( isLoggedIn ) )
            )
            .subscribe( ( results ) => {
                // const selectedTenant = results.tenants.find(t => t.oid === tenantID);
                const account = {
                    OID,
                    role,
                    name,
                    avatarURL: results.user.avatarURL ?? 'avatar_placeholder',
                    email: results.user.email,
                    alternativeEmails: results.user.alternativeEmails,
                    phone: results.user.phone,
                    organizationName,
                    organization: results.organization,
                    organizationOID,
                    // tenants: results.tenants,
                    // selectedTenant: selectedTenant || results.tenants[0],
                    formatLocale: results.user.formatLocale ?? this.locale,
                    preferredLanguage: results.user.preferredLanguage ?? navigator.language
                };

                /** set account */
                this.#userState.setState( account );
                this.localStorageService.save( 'account', account );

                /** handle case where language differs in user profile */
                const language = account.preferredLanguage.slice( 0, 2 );
                this.#cookieService.set( 'language', language, undefined, '/' );

                // if language differs force page reload after delay,
                // unless in local dev enironemnt
                const languageChanged = language !== document.documentElement.lang;
                if ( languageChanged && environment.name !== 'local' ) {
                    window.setTimeout( () => this.windowReload(), 200 );
                }
                isLoggedIn = true;
            } );
    }

    clearState() {
        this.isLoggedIn.set( false );
        this.#userState.setState( {} );
        this.#cookieService.delete( 'language' );
        localStorage.clear();
    }

    handleLogin() {
        this.#cookieService.delete( 'login' );
        this.msalService.loginRedirect( loginRequest );
    }

    /**
     * helper function
     * log out and cleanup session storages
     */
    handleLogout() {
        this.clearState();

        /** redirect to home page */
        this.#router.navigateByUrl( '/', {
            replaceUrl: true
        } );
    }

    /**
     * helper function to handle different MSAL events
     * which may occur at login / logout or token acquire
     */
    private handleMsalEvents() {
        this.#broadcastService.msalSubject$
            .pipe(
                filter(
                    ( msg: EventMessage ) =>
                        msg.eventType === EventType.LOGIN_SUCCESS ||
                        msg.eventType === EventType.LOGIN_FAILURE ||
                        msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
                        msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE ||
                        msg.eventType === EventType.LOGOUT_SUCCESS
                ),
                takeUntil( this.#destroyed )
            )
            .subscribe( ( result: EventMessage ) => {
                /** broadcast logout state */
                if (
                    result.eventType === EventType.LOGOUT_SUCCESS ||
                    result.eventType === EventType.LOGIN_FAILURE ||
                    result.eventType === EventType.ACQUIRE_TOKEN_FAILURE
                ) {
                    const autoLogin = this.#cookieService.check( 'login' );
                    if ( autoLogin ) {
                        this.handleLogin();
                    }
                    else {
                        this.handleLogout();
                    }
                } else if ( result.eventType === EventType.LOGIN_SUCCESS ) {
                    const { account } = result.payload as any;
                    this.handleAuthResponse( account );
                    this.retrieveAccessToken();
                } else if ( result.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ) {
                    /** recycle account information */
                    const account = this.localStorageService.load( 'account' );
                    this.#userState.setState( account );
                }
            } );
    }

    /**
     * helper function to update account information
     * with changes in user profile
     * @param {object} settings properties to be updated
     */
    updateAccount( settings: any ) {
        const account = this.localStorageService.load( 'account' );
        const updatedAccount = { ...account, ...settings };

        this.localStorageService.save( 'account', updatedAccount );
        this.#userState.setState( updatedAccount );
    }

    /** helper function to reload window */
    windowReload() {
        window.location.reload();
    }

    /**
     * handle app destroy
     * and unsubscribe from detroy handler
     */
    ngOnDestroy(): void {
        this.#destroyed.next( undefined );
        this.#destroyed.complete();
    }
}
